import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Check, X, ExternalLink } from 'lucide-react';
import { getConsent, saveConsent, isConsentOutdated } from '../../utils/consentManager';
import api from '../../api/api';
import './CookieConsent.css';

const DEFAULT_BANNER_CONFIG = {
  banner_title: 'We Value Your Privacy & Health Data',
  banner_description: 'MusB Diagnostics uses necessary cookies to ensure our diagnostic ordering and specimen portal operates securely and reliably. With your permission, we also use optional cookies for performance analytics and tailored healthcare communication in strict accordance with HIPAA, GDPR, and US state privacy regulations.',
  cookie_policy_url: '/cookie-policy',
  banner_version: '1.0'
};

const CookieConsentModal = () => {
  const [visible, setVisible] = useState(false);
  const [config, setConfig] = useState(DEFAULT_BANNER_CONFIG);
  const [activeVersion, setActiveVersion] = useState('1.0');

  // Fetch active config from CMS
  useEffect(() => {
    let isMounted = true;
    const fetchConfig = async () => {
      let forceShow = false;
      let forceReset = false;
      if (typeof window !== 'undefined' && window.location) {
        const params = new URLSearchParams(window.location.search);
        forceShow = params.get('cookie_banner') === '1' || params.get('cookie_settings') === '1';
        forceReset = params.get('reset_cookies') === '1';
        if (forceReset) {
          try {
            localStorage.removeItem('musb_cookie_consent');
            document.cookie = 'musb_consent_status=; Max-Age=0; path=/;';
          } catch (e) {}
        }
      }

      try {
        const res = await api.get('/api/cms/cookie-consent/');
        if (isMounted && res.data && res.data.config) {
          const cfg = res.data.config;
          setConfig(prev => ({
            ...prev,
            ...cfg
          }));
          const ver = cfg.banner_version || '1.0';
          setActiveVersion(ver);

          const stored = forceReset ? null : getConsent();
          if (forceShow || forceReset || !stored || isConsentOutdated(ver)) {
            setVisible(true);
          }
        }
      } catch (e) {
        const stored = forceReset ? null : getConsent();
        if (forceShow || forceReset || !stored) {
          setVisible(true);
        }
      }
    };

    fetchConfig();

    window.openMusBCookieSettings = () => {
      setVisible(true);
    };

    window.resetMusBCookieConsent = () => {
      try {
        localStorage.removeItem('musb_cookie_consent');
        document.cookie = 'musb_consent_status=; Max-Age=0; path=/;';
      } catch (e) {}
      setVisible(true);
    };

    const handleSettingsEvent = () => setVisible(true);
    const handleResetEvent = () => window.resetMusBCookieConsent();
    window.addEventListener('musb_open_cookie_settings', handleSettingsEvent);
    window.addEventListener('musb_reset_cookie_consent', handleResetEvent);

    return () => {
      isMounted = false;
      delete window.openMusBCookieSettings;
      delete window.resetMusBCookieConsent;
      window.removeEventListener('musb_open_cookie_settings', handleSettingsEvent);
      window.removeEventListener('musb_reset_cookie_consent', handleResetEvent);
    };
  }, []);

  const handleAcceptAll = async () => {
    await saveConsent({
      status: 'accepted_all',
      categories: { necessary: true, preferences: true, analytics: true, marketing: true },
      version: activeVersion
    });
    setVisible(false);
  };

  const handleNecessaryOnly = async () => {
    await saveConsent({
      status: 'necessary_only',
      categories: { necessary: true, preferences: true, analytics: true, marketing: true },
      version: activeVersion
    });
    setVisible(false);
  };

  const handleRejectOptional = async () => {
    await saveConsent({
      status: 'rejected_optional',
      categories: { necessary: true, preferences: true, analytics: true, marketing: true },
      version: activeVersion
    });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="musb-cookie-consent-overlay" role="dialog" aria-modal="true" aria-labelledby="musb-cookie-title">
      <div className="musb-cookie-card musb-cookie-card-horizontal">
        
        {/* Left / Info Section */}
        <div className="musb-cookie-info-col">
          <div className="musb-cookie-header-inline">
            <div className="musb-cookie-icon-pill">
              <Cookie size={16} className="text-cyan-400" />
            </div>
            <h3 id="musb-cookie-title" className="musb-cookie-title">
              {config.banner_title || 'We Value Your Privacy & Health Data'}
            </h3>
            <span className="musb-cookie-version-tag">
              Policy v{activeVersion} • HIPAA &amp; Privacy Compliant
            </span>
          </div>

          <div className="musb-cookie-body-inline">
            <p className="musb-cookie-description">
              {config.banner_description}
            </p>
            <div className="musb-cookie-links-row">
              <Link to={config.cookie_policy_url || '/cookie-policy'} className="musb-cookie-text-link" target="_blank" rel="noopener noreferrer">
                <span>Read Full Cookie Policy</span>
                <ExternalLink size={11} />
              </Link>
              <span className="musb-cookie-dot">•</span>
              <Link to="/privacy" className="musb-cookie-text-link" target="_blank" rel="noopener noreferrer">
                <span>Privacy Policy</span>
                <ExternalLink size={11} />
              </Link>
            </div>
          </div>
        </div>

        {/* Right / Actions Group */}
        <div className="musb-cookie-actions-group">
          <button 
            type="button" 
            className="musb-cookie-btn musb-btn-ghost"
            onClick={handleRejectOptional}
            title="Reject all optional cookies"
          >
            <span>Reject All Optional</span>
          </button>

          <button 
            type="button" 
            className="musb-cookie-btn musb-btn-ghost"
            onClick={handleNecessaryOnly}
            title="Allow necessary cookies only"
          >
            <span>Allow Necessary Only</span>
          </button>

          <button
            type="button"
            className="musb-cookie-btn musb-btn-accept-all"
            onClick={handleAcceptAll}
            title="Accept all cookies and continue"
          >
            <Check size={14} />
            <span>Accept All Cookies</span>
          </button>

          <button
            type="button"
            className="musb-cookie-close-btn"
            onClick={handleNecessaryOnly}
            aria-label="Close cookie consent banner"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

      </div>
    </div>
  );
};

export default CookieConsentModal;
